import React, { useEffect, useState } from "react";
import { useAppStore, formatGestureName } from "../store/useAppStore";
import Pagination from "./Pagination";
import SignTranslationUsageModal from "./SignTranslationUsageModal";
import UniversalModal from "./items/UniversalModal";

const isPrimitive = (value) => {
  return value === null || ["string", "number", "boolean"].includes(typeof value);
};

const fieldLabel = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
};

const primitiveValue = (value) => {
  if (value === null || typeof value === "undefined") return "-";
  if (typeof value === "boolean") return value ? "Да" : "Нет";
  return String(value);
};

const objectSummary = (value) => {
  if (Array.isArray(value)) return `Массив (${value.length})`;
  if (!value || typeof value !== "object") return primitiveValue(value);

  if (value.url) return `URL: ${value.url}`;
  if (value.name) return `Name: ${value.name}`;
  if (value.id) return `ID: ${value.id}`;
  return `Объект (${Object.keys(value).length} полей)`;
};

const normalizeEntityList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (Array.isArray(value?.data)) {
    return value.data
      .map((item) => (item?.attributes ? { id: item.id, ...item.attributes } : item))
      .filter(Boolean);
  }
  return [];
};

const pickFirstNonEmptyRelation = (...values) => {
  for (const value of values) {
    const normalized = normalizeEntityList(value);
    if (normalized.length) return normalized;
  }
  return [];
};

const appendUniqueEntity = (list, entity) => {
  const source = Array.isArray(list) ? list : [];
  if (!entity) return source;

  const exists = source.some((item) => {
    if (!item || !entity) return false;
    if (entity?.documentId && item?.documentId) {
      return String(item.documentId) === String(entity.documentId);
    }
    return Number(item?.id) === Number(entity?.id);
  });

  if (exists) return source;
  return [...source, entity];
};

const normalizeAssociationEntity = (item) => {
  if (!item) return null;

  const source = item?.attributes ? { id: item.id, ...item.attributes } : item;
  const normalizeRelation = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) {
      return value.data
        .map((relation) => (relation?.attributes ? { id: relation.id, ...relation.attributes } : relation))
        .filter(Boolean);
    }
    return [];
  };

  return {
    ...source,
    signs: normalizeRelation(source?.signs),
    words: normalizeRelation(source?.words),
  };
};

const associationSignature = (association) => {
  if (!association) return "";

  const signsKey = normalizeEntityList(association?.signs)
    .map((item) => item?.documentId || item?.id)
    .filter(Boolean)
    .join("|");

  const wordsKey = normalizeEntityList(association?.words)
    .map((item) => item?.documentId || item?.id)
    .filter(Boolean)
    .join("|");

  return `${association?.id || ""}:${association?.documentId || ""}:s=${signsKey}:w=${wordsKey}`;
};

const buildAssociationPreview = (association) => {
  const signs = normalizeEntityList(association?.signs);
  const words = normalizeEntityList(association?.words);

  return {
    id: association?.id,
    documentId: association?.documentId,
    signs,
    words,
  };
};

const GesturesPage = () => {
  const PAGE_SIZE_OPTIONS = [6, 12, 24];

  const signs = useAppStore((state) => state.signs);
  const signsLoading = useAppStore((state) => state.signsLoading);
  const signsError = useAppStore((state) => state.signsError);
  const signsPagination = useAppStore((state) => state.signsPagination);
  const apiBase = useAppStore((state) => state.API);
  const token = useAppStore((state) => state.token);
  const createSignLoading = useAppStore((state) => state.createSignLoading);
  const createSignError = useAppStore((state) => state.createSignError);
  const deleteSignLoading = useAppStore((state) => state.deleteSignLoading);
  const deleteSignError = useAppStore((state) => state.deleteSignError);
  const signVideosById = useAppStore((state) => state.signVideosById);
  const signVideosError = useAppStore((state) => state.signVideosError);
  const signVideoUploadLoading = useAppStore((state) => state.signVideoUploadLoading);
  const signVideoUploadError = useAppStore((state) => state.signVideoUploadError);
  const wordAssociationsById = useAppStore((state) => state.wordAssociationsById);
  const wordAssociationsError = useAppStore((state) => state.wordAssociationsError);
  const associationSignsSearchResults = useAppStore((state) => state.associationSignsSearchResults);
  const associationSignsSearchLoading = useAppStore((state) => state.associationSignsSearchLoading);
  const associationSignsSearchError = useAppStore((state) => state.associationSignsSearchError);
  const associationSignsMutationLoading = useAppStore((state) => state.associationSignsMutationLoading);
  const associationSignsMutationError = useAppStore((state) => state.associationSignsMutationError);
  const associationWordsSearchResults = useAppStore((state) => state.associationWordsSearchResults);
  const associationWordsSearchLoading = useAppStore((state) => state.associationWordsSearchLoading);
  const associationWordsSearchError = useAppStore((state) => state.associationWordsSearchError);
  const associationWordsMutationLoading = useAppStore((state) => state.associationWordsMutationLoading);
  const associationWordsMutationError = useAppStore((state) => state.associationWordsMutationError);
  const fetchSigns = useAppStore((state) => state.fetchSigns);
  const createSign = useAppStore((state) => state.createSign);
  const deleteSign = useAppStore((state) => state.deleteSign);
  const fetchSignVideosByIds = useAppStore((state) => state.fetchSignVideosByIds);
  const uploadSignVideoFile = useAppStore((state) => state.uploadSignVideoFile);
  const removeSignVideoFile = useAppStore((state) => state.removeSignVideoFile);
  const updateSignVideoSimilar = useAppStore((state) => state.updateSignVideoSimilar);
  const createSignVideoForSign = useAppStore((state) => state.createSignVideoForSign);
  const fetchWordAssociationsByIds = useAppStore((state) => state.fetchWordAssociationsByIds);
  const searchAssociationSigns = useAppStore((state) => state.searchAssociationSigns);
  const attachSignToAssociation = useAppStore((state) => state.attachSignToAssociation);
  const removeSignFromAssociation = useAppStore((state) => state.removeSignFromAssociation);
  const searchAssociationWords = useAppStore((state) => state.searchAssociationWords);
  const attachWordToAssociation = useAppStore((state) => state.attachWordToAssociation);
  const removeWordFromAssociation = useAppStore((state) => state.removeWordFromAssociation);
  const createWordInAssociation = useAppStore((state) => state.createWordInAssociation);
  const signUsageLoading = useAppStore((state) => state.signUsageLoading);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [newSignName, setNewSignName] = useState("");
  const [newSignId, setNewSignId] = useState("");
  const [createSignPanelOpen, setCreateSignPanelOpen] = useState(false);
  const [associationModalOpen, setAssociationModalOpen] = useState(false);
  const [activeAssociation, setActiveAssociation] = useState(null);
  const [associationSignSearchInput, setAssociationSignSearchInput] = useState("");
  const [associationWordSearchInput, setAssociationWordSearchInput] = useState("");
  const [createAssociationSignId, setCreateAssociationSignId] = useState("");
  const [signAssociationModalOpen, setSignAssociationModalOpen] = useState(false);
  const [usageSign, setUsageSign] = useState(null);
  const [activeSignForAssociation, setActiveSignForAssociation] = useState(null);
  const [signAssociationSearchType, setSignAssociationSearchType] = useState("signs");
  const [signAssociationSearchInput, setSignAssociationSearchInput] = useState("");
  const [signAssociationResults, setSignAssociationResults] = useState([]);
  const [signAssociationSearchLoading, setSignAssociationSearchLoading] = useState(false);
  const [signAssociationSearchError, setSignAssociationSearchError] = useState("");
  const [signAssociationMutationLoading, setSignAssociationMutationLoading] = useState(false);
  const [signAssociationMutationError, setSignAssociationMutationError] = useState("");
  const [pendingVideoFilesByKey, setPendingVideoFilesByKey] = useState({});
  const [pendingCreateVideoFilesBySignKey, setPendingCreateVideoFilesBySignKey] = useState({});

  useEffect(() => {
    fetchSigns(currentPage, itemsPerPage, searchTerm, searchField);
  }, [fetchSigns, currentPage, itemsPerPage, searchTerm, searchField]);

  useEffect(() => {
    const nextQuery = searchInput.trim();
    const normalized =
      searchField === "sign_id" ? nextQuery : formatGestureName(nextQuery);
    if (normalized === searchTerm) return undefined;

    const timer = setTimeout(() => {
      setCurrentPage(1);
      setSearchTerm(normalized);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput, searchTerm, searchField]);

  useEffect(() => {
    const ids = signs
      .flatMap((sign) => (Array.isArray(sign?.sign_videos) ? sign.sign_videos : []))
      .map((video) => video?.id)
      .filter(Boolean);

    if (ids.length) {
      fetchSignVideosByIds(ids);
    }
  }, [signs, fetchSignVideosByIds]);

  useEffect(() => {
    const ids = signs
      .flatMap((sign) => (Array.isArray(sign?.word_associations) ? sign.word_associations : []))
      .map((item) => item?.id)
      .filter(Boolean);

    if (ids.length) {
      fetchWordAssociationsByIds(ids);
    }
  }, [signs, fetchWordAssociationsByIds]);

  useEffect(() => {
    if (!associationModalOpen) return undefined;

    const formatted = formatGestureName(associationSignSearchInput);
    const timer = setTimeout(() => {
      if (formatted) {
        searchAssociationSigns(formatted);
      } else {
        searchAssociationSigns("");
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [associationModalOpen, associationSignSearchInput, searchAssociationSigns]);

  useEffect(() => {
    if (!associationModalOpen) return undefined;

    const formatted = formatGestureName(associationWordSearchInput);
    const timer = setTimeout(() => {
      if (formatted) {
        searchAssociationWords(formatted);
      } else {
        searchAssociationWords("");
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [associationModalOpen, associationWordSearchInput, searchAssociationWords]);

  useEffect(() => {
    if (!signAssociationModalOpen) return undefined;

    const formatted = formatGestureName(signAssociationSearchInput);
    const timer = setTimeout(async () => {
      if (!formatted) {
        setSignAssociationResults([]);
        setSignAssociationSearchError("");
        return;
      }

      const base = apiBase.replace(/\/$/, "");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      params.append("pagination[page]", "1");
      params.append("pagination[pageSize]", "10");
      params.append("populate", "signs");
      params.append("populate", "words");

      if (signAssociationSearchType === "signs") {
        params.append("filters[signs][name][$contains][0]", formatted);
      } else {
        params.append("filters[words][lemma][$contains][0]", formatted);
      }

      setSignAssociationSearchLoading(true);
      setSignAssociationSearchError("");

      const fetchFrom = async (endpoint) => {
        const resp = await fetch(`${base}${endpoint}?${params.toString()}`, { headers });
        if (!resp.ok) {
          let message = "Ошибка поиска ассоциаций";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return resp.json();
      };

      try {
        let payload;
        try {
          payload = await fetchFrom("/word-associations");
        } catch {
          payload = await fetchFrom("/api/word-associations");
        }

        const items = Array.isArray(payload) ? payload : (payload?.data || []);
        const normalized = items.map((item) => normalizeAssociationEntity(item)).filter(Boolean);
        setSignAssociationResults(normalized);
      } catch (error) {
        setSignAssociationResults([]);
        setSignAssociationSearchError(error.message || "Ошибка поиска ассоциаций");
      } finally {
        setSignAssociationSearchLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [
    signAssociationModalOpen,
    signAssociationSearchInput,
    signAssociationSearchType,
    apiBase,
    token,
  ]);

  const totalPages = Math.max(1, signsPagination?.pageCount || 1);
  const associationFromSigns = activeAssociation
    ? signs
      .flatMap((item) => (Array.isArray(item?.word_associations) ? item.word_associations : []))
      .find((item) => {
        if (!item) return false;
        if (activeAssociation?.documentId && item?.documentId) {
          return String(item.documentId) === String(activeAssociation.documentId);
        }
        if (activeAssociation?.id && item?.id) {
          return Number(item.id) === Number(activeAssociation.id);
        }
        return false;
      })
    : null;

  const associationFromStore = activeAssociation?.id
    ? (wordAssociationsById[activeAssociation.id] || wordAssociationsById[Number(activeAssociation.id)] || null)
    : null;

  const preferredAssociation = associationFromStore || associationFromSigns || null;

  useEffect(() => {
    if (!associationModalOpen || !preferredAssociation) return;

    setActiveAssociation((prev) => {
      const prevSignature = associationSignature(prev);
      const nextSignature = associationSignature(preferredAssociation);
      if (prevSignature === nextSignature) return prev;

      return {
        ...(prev || {}),
        ...preferredAssociation,
      };
    });
  }, [associationModalOpen, preferredAssociation]);

  const currentAssociation = activeAssociation || preferredAssociation || null;

  const currentAssociationSigns = pickFirstNonEmptyRelation(
    currentAssociation?.signs,
    associationFromStore?.signs,
    associationFromSigns?.signs
  );

  const currentAssociationWords = pickFirstNonEmptyRelation(
    currentAssociation?.words,
    associationFromStore?.words,
    associationFromSigns?.words
  );

  const associationWordQuery = formatGestureName(associationWordSearchInput);
  const hasExactAssociationWordMatch = !!associationWordQuery && associationWordsSearchResults.some((item) => {
    const lemma = String(item?.lemma || "").trim().toLowerCase();
    return lemma === associationWordQuery.toLowerCase();
  });

  const currentSignAssociations = normalizeEntityList(activeSignForAssociation?.word_associations)
    .map((item) => {
      const full = wordAssociationsById[item?.id] || item;
      return normalizeAssociationEntity(full);
    })
    .filter(Boolean);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event) => {
    const value = Number(event.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleCreateSign = async () => {
    const created = await createSign({ name: newSignName, sign_id: newSignId });
    if (!created) return;

    setNewSignName("");
    setNewSignId("");

    if (currentPage === 1) {
      fetchSigns(1, itemsPerPage, searchTerm, searchField);
    } else {
      setCurrentPage(1);
    }
  };

  const handleDeleteSign = async (sign) => {
    const signTitle = sign?.name || sign?.lemma || sign?.sign_id || `#${sign?.id}`;
    const confirmed = window.confirm(`Удалить жест ${signTitle}?`);
    if (!confirmed) return;

    const ok = await deleteSign(sign);
    if (!ok) return;

    const nextPage = signs.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    } else {
      fetchSigns(currentPage, itemsPerPage, searchTerm, searchField);
    }
  };

  const refreshAssociationViews = async (association) => {
    await fetchSigns(currentPage, itemsPerPage, searchTerm, searchField);

    const associationId = association?.id;
    const associationDocumentId = association?.documentId;

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const fetchAssociationDirect = async (key) => {
      const params = new URLSearchParams();
      params.append("populate", "signs");
      params.append("populate", "words");
      const url = `${apiBase.replace(/\/$/, "")}/word-associations/${key}?${params.toString()}`;
      const resp = await fetch(url, { headers: authHeaders });

      if (!resp.ok) {
        const apiUrl = `${apiBase.replace(/\/$/, "")}/api/word-associations/${key}?${params.toString()}`;
        const apiResp = await fetch(apiUrl, { headers: authHeaders });
        if (!apiResp.ok) return null;
        const apiPayload = await apiResp.json();
        return normalizeAssociationEntity(apiPayload?.data || apiPayload);
      }

      const payload = await resp.json();
      return normalizeAssociationEntity(payload?.data || payload);
    };

    const keys = Array.from(new Set([associationDocumentId, associationId].filter(Boolean)));
    for (const key of keys) {
      const entity = await fetchAssociationDirect(key);
      if (entity) {
        setActiveAssociation(entity);
        return;
      }
    }

    if (associationId) {
      const refreshed = await fetchWordAssociationsByIds([associationId], true);
      const nextAssociation = refreshed?.[associationId] || refreshed?.[Number(associationId)] || null;
      if (nextAssociation) setActiveAssociation(nextAssociation);
    }
  };

  const openAssociationModal = (association) => {
    setActiveAssociation(association || null);
    setAssociationSignSearchInput("");
    setAssociationWordSearchInput("");
    setCreateAssociationSignId("");
    setAssociationModalOpen(true);
  };

  const closeAssociationModal = () => {
    setAssociationModalOpen(false);
    setActiveAssociation(null);
    setAssociationSignSearchInput("");
    setAssociationWordSearchInput("");
    setCreateAssociationSignId("");
  };

  const openSignAssociationModal = (sign) => {
    setActiveSignForAssociation(sign || null);
    setSignAssociationSearchInput("");
    setSignAssociationSearchType("signs");
    setSignAssociationResults([]);
    setSignAssociationSearchError("");
    setSignAssociationMutationError("");
    setSignAssociationModalOpen(true);
  };

  const closeSignAssociationModal = () => {
    setSignAssociationModalOpen(false);
    setActiveSignForAssociation(null);
    setSignAssociationSearchInput("");
    setSignAssociationResults([]);
    setSignAssociationSearchError("");
    setSignAssociationMutationError("");
  };

  const refreshSignAssociationViews = async (sign) => {
    const refreshedSigns = await fetchSigns(currentPage, itemsPerPage, searchTerm, searchField);
    const targetSign = Array.isArray(refreshedSigns)
      ? refreshedSigns.find((item) => {
        if (sign?.documentId && item?.documentId) {
          return String(item.documentId) === String(sign.documentId);
        }
        return Number(item?.id) === Number(sign?.id);
      })
      : null;

    if (targetSign) {
      setActiveSignForAssociation(targetSign);
      const associationIds = normalizeEntityList(targetSign?.word_associations)
        .map((item) => item?.id)
        .filter((id) => Number.isFinite(Number(id)));

      if (associationIds.length) {
        await fetchWordAssociationsByIds(associationIds, true);
      }
    }
  };

  const handleAttachAssociationToSign = async (sign, association) => {
    if (!sign || !association) return;

    setActiveSignForAssociation((prev) => {
      if (!prev) return prev;
      const prevAssociations = normalizeEntityList(prev?.word_associations);
      return { ...prev, word_associations: appendUniqueEntity(prevAssociations, association) };
    });

    const signKey = sign?.documentId || sign?.id;
    const signId = sign?.id;
    const signDocumentId = sign?.documentId;
    const associationId = association?.id;
    const associationDocumentId = association?.documentId;

    if (!signKey || (!associationId && !associationDocumentId)) {
      setSignAssociationMutationError("Не найдены идентификаторы для привязки ассоциации");
      await refreshSignAssociationViews(sign);
      return;
    }

    const base = apiBase.replace(/\/$/, "");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const payloads = [
      ...(associationDocumentId ? [{ data: { word_associations: { connect: [associationDocumentId] } } }] : []),
      ...(associationDocumentId ? [{ data: { word_associations: { connect: [{ documentId: associationDocumentId }] } } }] : []),
      ...(associationId ? [{ data: { word_associations: { connect: [associationId] } } }] : []),
      ...(associationId ? [{ data: { word_associations: { connect: [{ id: associationId }] } } }] : []),
      ...(associationId ? [{ data: { word_associations: [associationId] } }] : []),
    ];

    const signKeys = Array.from(new Set([signDocumentId, signId].filter(Boolean)));

    setSignAssociationMutationLoading(true);
    setSignAssociationMutationError("");

    try {
      let lastError = null;
      for (const key of signKeys) {
        for (const endpoint of [`/signss/${key}`, `/api/signss/${key}`]) {
          for (const payload of payloads) {
            try {
              const resp = await fetch(base + endpoint, {
                method: "PUT",
                headers,
                body: JSON.stringify(payload),
              });

              if (!resp.ok) {
                let message = "Ошибка привязки ассоциации к жесту";
                try {
                  const errData = await resp.json();
                  message = errData?.error?.message || message;
                } catch {
                  const text = await resp.text();
                  if (text) message = text;
                }
                throw new Error(message);
              }

              await refreshSignAssociationViews(sign);
              return;
            } catch (error) {
              lastError = error;
            }
          }
        }
      }

      throw lastError || new Error("Ошибка привязки ассоциации к жесту");
    } catch (error) {
      setSignAssociationMutationError(error.message || "Ошибка привязки ассоциации к жесту");
      await refreshSignAssociationViews(sign);
    } finally {
      setSignAssociationMutationLoading(false);
    }
  };

  const handleCreateAssociationForSign = async (sign) => {
    if (!sign) return;

    const signId = sign?.id;
    const signDocumentId = sign?.documentId;
    const base = apiBase.replace(/\/$/, "");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    setSignAssociationMutationLoading(true);
    setSignAssociationMutationError("");

    const payloadVariants = [
      ...(signDocumentId ? [{ linked: true, body: { data: { signs: { connect: [signDocumentId] } } } }] : []),
      ...(signDocumentId ? [{ linked: true, body: { data: { signs: { connect: [{ documentId: signDocumentId }] } } } }] : []),
      ...(signId ? [{ linked: true, body: { data: { signs: { connect: [signId] } } } }] : []),
      ...(signId ? [{ linked: true, body: { data: { signs: { connect: [{ id: signId }] } } } }] : []),
      ...(signDocumentId ? [{ linked: true, body: { data: { signss: { connect: [signDocumentId] } } } }] : []),
      ...(signDocumentId ? [{ linked: true, body: { data: { signss: { connect: [{ documentId: signDocumentId }] } } } }] : []),
      ...(signId ? [{ linked: true, body: { data: { signss: { connect: [signId] } } } }] : []),
      ...(signId ? [{ linked: true, body: { data: { signss: { connect: [{ id: signId }] } } } }] : []),
      { linked: false, body: { data: {} } },
    ];

    try {
      let createdAssociation = null;
      let createdLinked = false;
      let lastError = null;

      for (const endpoint of ["/word-associations", "/api/word-associations"]) {
        for (const variant of payloadVariants) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "POST",
              headers,
              body: JSON.stringify(variant.body),
            });

            if (!resp.ok) {
              let message = "Ошибка создания association";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || errData?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            const result = await resp.json();
            createdAssociation = normalizeAssociationEntity(result?.data || result);
            createdLinked = variant.linked;
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
          }
        }

        if (createdAssociation) break;
      }

      if (!createdAssociation) {
        throw lastError || new Error("Ошибка создания association");
      }

      if (createdAssociation?.id) {
        await fetchWordAssociationsByIds([createdAssociation.id], true);
      }

      if (!createdLinked) {
        setSignAssociationMutationLoading(false);
        await handleAttachAssociationToSign(sign, createdAssociation);
        return;
      }

      await refreshSignAssociationViews(sign);
      setSignAssociationResults((prev) => appendUniqueEntity(prev, createdAssociation));
    } catch (error) {
      setSignAssociationMutationError(error.message || "Ошибка создания association");
    } finally {
      setSignAssociationMutationLoading(false);
    }
  };

  const handleAttachExistingSign = async (association, sign) => {
    setActiveAssociation((prev) => {
      if (!prev) return prev;
      const prevSigns = normalizeEntityList(prev?.signs);
      return { ...prev, signs: appendUniqueEntity(prevSigns, sign) };
    });

    const ok = await attachSignToAssociation(association, sign);
    if (!ok) {
      await refreshAssociationViews(association);
      return;
    }
    await refreshAssociationViews(association);
  };

  const handleCreateAndAttachSign = async () => {
    if (!activeAssociation) return;

    const formattedName = formatGestureName(associationSignSearchInput);
    if (!formattedName) return;

    const created = await createSign({ name: formattedName, sign_id: createAssociationSignId });
    if (!created) return;

    setActiveAssociation((prev) => {
      if (!prev) return prev;
      const prevSigns = normalizeEntityList(prev?.signs);
      return { ...prev, signs: appendUniqueEntity(prevSigns, created) };
    });

    const ok = await attachSignToAssociation(activeAssociation, created);
    if (!ok) {
      await refreshAssociationViews(activeAssociation);
      return;
    }
    await refreshAssociationViews(activeAssociation);
  };

  const handleAttachExistingWord = async (association, word) => {
    setActiveAssociation((prev) => {
      if (!prev) return prev;
      const prevWords = normalizeEntityList(prev?.words);
      return { ...prev, words: appendUniqueEntity(prevWords, word) };
    });

    const ok = await attachWordToAssociation(association, word);
    if (!ok) {
      await refreshAssociationViews(association);
      return;
    }
    await refreshAssociationViews(association);
  };

  const handleCreateAndAttachWord = async () => {
    if (!activeAssociation) return;

    const formattedLemma = formatGestureName(associationWordSearchInput);
    if (!formattedLemma) return;

    const created = await createWordInAssociation(activeAssociation, formattedLemma);
    if (!created) return;

    setActiveAssociation((prev) => {
      if (!prev) return prev;
      const prevWords = normalizeEntityList(prev?.words);
      return { ...prev, words: appendUniqueEntity(prevWords, created) };
    });

    await refreshAssociationViews(activeAssociation);
    setAssociationWordSearchInput("");
  };

  const handleRemoveAssociatedSign = async (association, sign) => {
    const signLabel = sign?.name || sign?.lemma || `#${sign?.id || "-"}`;
    const confirmed = window.confirm(`Удалить жест ${signLabel} из association?`);
    if (!confirmed) return;

    setActiveAssociation((prev) => {
      if (!prev) return prev;

      const prevSigns = normalizeEntityList(prev?.signs);
      const nextSigns = prevSigns.filter((item) => {
        if (sign?.documentId && item?.documentId) {
          return String(item.documentId) !== String(sign.documentId);
        }
        return Number(item?.id) !== Number(sign?.id);
      });

      return { ...prev, signs: nextSigns };
    });

    const ok = await removeSignFromAssociation(association, sign);
    if (!ok) {
      await refreshAssociationViews(association);
      return;
    }
    await refreshAssociationViews(association);
  };

  const handleRemoveAssociatedWord = async (association, word) => {
    const wordLabel = word?.lemma || `#${word?.id || "-"}`;
    const confirmed = window.confirm(`Удалить word ${wordLabel} из association?`);
    if (!confirmed) return;

    setActiveAssociation((prev) => {
      if (!prev) return prev;

      const prevWords = normalizeEntityList(prev?.words);
      const nextWords = prevWords.filter((item) => {
        if (word?.documentId && item?.documentId) {
          return String(item.documentId) !== String(word.documentId);
        }
        return Number(item?.id) !== Number(word?.id);
      });

      return { ...prev, words: nextWords };
    });

    const ok = await removeWordFromAssociation(association, word);
    if (!ok) {
      await refreshAssociationViews(association);
      return;
    }
    await refreshAssociationViews(association);
  };

  const handleSignVideoFileSelected = (video, event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) return;

    const key = String(video?.id || video?.documentId || "");
    if (!key) return;

    setPendingVideoFilesByKey((prev) => ({
      ...prev,
      [key]: selectedFile,
    }));
  };

  const handleUploadSelectedSignVideoFile = async (video) => {
    const key = String(video?.id || video?.documentId || "");
    if (!key) return;

    const selectedFile = pendingVideoFilesByKey[key];
    if (!selectedFile) return;

    const ok = await uploadSignVideoFile(video, selectedFile);
    if (!ok) return;

    setPendingVideoFilesByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });



    if (video?.id) {
      await fetchSignVideosByIds([video.id], true);
    }

    await fetchSigns(currentPage, itemsPerPage, searchTerm, searchField);
  };

  const handleRemoveSignVideoFile = async (video) => {
    const videoFiles = Array.isArray(video?.video) ? video.video : [];
    if (!videoFiles.length) return;

    const fileName = videoFiles[videoFiles.length - 1]?.name || "файл";
    const confirmed = window.confirm(`Удалить ${fileName} с сервера?`);
    if (!confirmed) return;

    const ok = await removeSignVideoFile(video);
    if (!ok) return;

    const key = String(video?.id || video?.documentId || "");
    if (key) {
      setPendingVideoFilesByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }

    if (video?.id) {
      await fetchSignVideosByIds([video.id], true);
    }
    await fetchSigns(currentPage, itemsPerPage, searchTerm, searchField);
  };

  const handleToggleSignVideoSimilar = async (video) => {
    const currentSimilar = !!(video?.metadata?.similar ?? video?.similar);
    const ok = await updateSignVideoSimilar(video, !currentSimilar);
    if (!ok) return;

    if (video?.id) {
      await fetchSignVideosByIds([video.id], true);
    }
  };

  const handleCreateSignVideoFileSelected = (sign, event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) return;

    const signKey = String(sign?.id || sign?.documentId || "");
    if (!signKey) return;

    setPendingCreateVideoFilesBySignKey((prev) => ({
      ...prev,
      [signKey]: selectedFile,
    }));
  };

  const handleCreateSignVideoAndUpload = async (sign) => {
    const signKey = String(sign?.id || sign?.documentId || "");
    if (!signKey) return;

    const selectedFile = pendingCreateVideoFilesBySignKey[signKey];
    if (!selectedFile) return;

    const createdSignVideo = await createSignVideoForSign(sign);
    if (!createdSignVideo) {
      return;
    }

    const ok = await uploadSignVideoFile(createdSignVideo, selectedFile);
    if (!ok) return;

    setPendingCreateVideoFilesBySignKey((prev) => {
      const next = { ...prev };
      delete next[signKey];
      return next;
    });

    await fetchSigns(currentPage, itemsPerPage, searchTerm, searchField);
    if (createdSignVideo?.id) {
      await fetchSignVideosByIds([createdSignVideo.id], true);
    }
  };

  return (
    <main className="main-page">
      <div className="main-card">
        <div className="requests-head">
          <h1>
            <i className="fa-solid fa-hands" aria-hidden="true"></i> Жесты
          </h1>
          <div className="requests-head-actions">
            <label className="gesture-search" htmlFor="gestures-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              <select
                value={searchField}
                onChange={(event) => {
                  setSearchField(event.target.value === "sign_id" ? "sign_id" : "name");
                  setCurrentPage(1);
                }}
                aria-label="Поле поиска"
              >
                <option value="name">name</option>
                <option value="sign_id">sign_id</option>
              </select>
              <input
                id="gestures-search"
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={`Поиск по ${searchField}`}
              />
              {searchInput ? (
                <button className="gesture-search-clear" type="button" onClick={() => setSearchInput("")}> 
                  <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                </button>
              ) : null}
            </label>
            <label className="page-size" htmlFor="gestures-page-size">
              <span>На странице</span>
              <select id="gestures-page-size" value={itemsPerPage} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <button className="e-btn" type="button" onClick={() => fetchSigns(currentPage, itemsPerPage, searchTerm, searchField)}>
              <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <section className="create-sign-panel">
          <button
            className="e-btn sentences-create-toggle"
            type="button"
            onClick={() => setCreateSignPanelOpen((prev) => !prev)}
          >
            <i className={`fa-solid ${createSignPanelOpen ? "fa-chevron-up" : "fa-plus"}`} aria-hidden="true"></i>
            {createSignPanelOpen ? "Скрыть добавление жеста" : "Добавить жест"}
          </button>

          {createSignPanelOpen ? (
            <>
              <div className="create-sign-form">
                <label>
                  <span>Name</span>
                  <input
                    type="text"
                    value={newSignName}
                    onChange={(e) => setNewSignName(e.target.value)}
                    placeholder="Например: Волк"
                  />
                </label>
                <label>
                  <span>sign_id</span>
                  <input
                    type="text"
                    value={newSignId}
                    onChange={(e) => setNewSignId(e.target.value)}
                    placeholder="uuid или id"
                  />
                </label>
                <button className="e-btn" type="button" onClick={handleCreateSign} disabled={createSignLoading}>
                  <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i>
                  {createSignLoading ? "Сохраняем..." : "Создать"}
                </button>
              </div>
              {createSignError ? (
                <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {createSignError}</p>
              ) : null}
            </>
          ) : null}
        </section>

        {deleteSignError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {deleteSignError}</p>
        ) : null}

        {!signsLoading ? (
          <p>
            <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
            Всего: {signsPagination?.total || 0} | Страница: {signsPagination?.page || currentPage} из {totalPages}
            {searchTerm ? ` | Поиск (${searchField}): "${searchTerm}"` : ""}
          </p>
        ) : null}

        {signsLoading ? (
          <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Загрузка жестов...</p>
        ) : null}

        {signsError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signsError}</p>
        ) : null}

        {signVideosError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signVideosError}</p>
        ) : null}

        {signVideoUploadError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signVideoUploadError}</p>
        ) : null}

        {wordAssociationsError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {wordAssociationsError}</p>
        ) : null}

        {associationSignsMutationError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {associationSignsMutationError}</p>
        ) : null}

        {!signsLoading && !signs.length ? (
          <p>
            <i className="fa-regular fa-folder-open" aria-hidden="true"></i>
            {searchTerm ? " По вашему запросу ничего не найдено." : " Пока нет жестов."}
          </p>
        ) : null}

        {!!signs.length ? (
          <div className="requests-list">
            {signs.map((sign, index) => (
              <article className="requests-item" key={sign?.id || `sign-${index}`}>
                <div className="requests-item-top">
                  <div className="requests-item-lemma">
                    <i className="fa-solid fa-hand" aria-hidden="true"></i>
                    <span>{sign?.lemma || sign?.title || sign?.name || `Жест #${sign?.id || index + 1}`}</span>
                  </div>
                  <div className="gesture-actions">
                    <span className="status-badge new">{sign?.documentId ? `doc: ${sign.documentId}` : "—"}</span>
                    <button
                      className="e-btn requests-usage-btn"
                      type="button"
                      onClick={() => setUsageSign(sign)}
                      disabled={signUsageLoading}
                      title="Где используется жест"
                    >
                      <i className="fa-solid fa-link" aria-hidden="true"></i>
                      Где используется
                    </button>
                    <button
                      className="association-open-btn"
                      type="button"
                      onClick={() => openSignAssociationModal(sign)}
                    >
                      <i className="fa-solid fa-sitemap" aria-hidden="true"></i>
                      Ассоциации
                    </button>
                    <button
                      className="danger-btn"
                      type="button"
                      onClick={() => handleDeleteSign(sign)}
                      disabled={deleteSignLoading}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true"></i>
                      Удалить
                    </button>
                  </div>
                </div>

                <div className="gesture-grid">
                  {Object.entries(sign || {})
                    .filter(([key, value]) => key !== "id" && isPrimitive(value))
                    .map(([key, value]) => (
                      <div className="gesture-field" key={`field-${key}`}>
                        <span className="gesture-field-label">{fieldLabel(key)}</span>
                        <span className="gesture-field-value">{primitiveValue(value)}</span>
                      </div>
                    ))}
                </div>

                <div className="gesture-complex-list">
                  {Object.entries(sign || {})
                    .filter(([key, value]) => key !== "sign_videos" && key !== "word_associations" && key !== "signs" && key !== "words" && !isPrimitive(value))
                    .map(([key, value]) => (
                      <details className="gesture-complex-item" key={`complex-${key}`}>
                        <summary>
                          <span>{fieldLabel(key)}</span>
                          <em>{objectSummary(value)}</em>
                        </summary>
                        <pre className="gesture-json">{JSON.stringify(value, null, 2)}</pre>
                      </details>
                    ))}

                  <details className="gesture-complex-item">
                    <summary>
                      <span>Sign videos</span>
                      <em>Видео: {Array.isArray(sign?.sign_videos) ? sign.sign_videos.length : 0}</em>
                    </summary>
                    {Array.isArray(sign?.sign_videos) && sign.sign_videos.length ? (
                      <div className="sign-video-list">
                        {sign.sign_videos.map((videoRef) => {
                          const fullVideo = signVideosById[videoRef.id] || videoRef;
                          const videoFile = Array.isArray(fullVideo?.video)
                            ? (fullVideo.video[fullVideo.video.length - 1] || null)
                            : null;
                          const mediaUrl = videoFile?.url ? `${apiBase}${videoFile.url}` : "";
                          const videoKey = String(fullVideo?.id || videoRef?.id || "");

                          return (
                            <article className="sign-video-card" key={`sv-${videoRef.id}`}>
                              <div className="sign-video-title">
                                <i className="fa-solid fa-video" aria-hidden="true"></i>
                                <span>ID: {fullVideo?.id || "-"}</span>
                              </div>

                              <div className="sign-video-grid">
                                <div><strong>Document:</strong> {fullVideo?.documentId || "-"}</div>
                                <div><strong>Duration:</strong> {primitiveValue(fullVideo?.duration)}</div>
                                <div><strong>Name:</strong> {fullVideo?.metadata?.name || "-"}</div>
                                <div><strong>Source:</strong> {fullVideo?.metadata?.source || "-"}</div>
                                <div className="sign-video-similar-row">
                                  <strong>Similar:</strong>
                                  <label className="sign-video-similar-toggle">
                                    <input
                                      type="checkbox"
                                      checked={!!(fullVideo?.metadata?.similar ?? fullVideo?.similar)}
                                      onChange={() => handleToggleSignVideoSimilar(fullVideo)}
                                      disabled={signVideoUploadLoading}
                                    />
                                    <span>{(fullVideo?.metadata?.similar ?? fullVideo?.similar) ? "true" : "false"}</span>
                                  </label>
                                </div>
                                <div><strong>File:</strong> {videoFile?.name || "-"}</div>
                              </div>

                              {mediaUrl ? (
                                <video className="sign-video-player" controls preload="metadata" playsInline>
                                  <source src={mediaUrl} type={videoFile?.mime || "video/mp4"} />
                                  Ваш браузер не поддерживает тег video.
                                </video>
                              ) : null}

                              {mediaUrl ? (
                                <a className="sign-video-link" href={mediaUrl} target="_blank" rel="noreferrer">
                                  Открыть видео
                                </a>
                              ) : null}

                              <div className="sign-video-actions">
                                <input
                                  id={`sign-video-upload-${videoKey}`}
                                  className="sign-video-upload-input"
                                  type="file"
                                  accept="video/*"
                                  onChange={(event) => handleSignVideoFileSelected(fullVideo, event)}
                                  disabled={signVideoUploadLoading}
                                />
                                <button
                                  className="e-btn sign-video-upload-btn"
                                  type="button"
                                  onClick={() => handleUploadSelectedSignVideoFile(fullVideo)}
                                  disabled={signVideoUploadLoading || !pendingVideoFilesByKey[videoKey]}
                                >
                                  <i className="fa-solid fa-upload" aria-hidden="true"></i>
                                  {signVideoUploadLoading ? "Загрузка..." : "Отправить на сервер"}
                                </button>
                                {pendingVideoFilesByKey[videoKey] ? (
                                  <p className="sign-video-pending-file">Выбран файл: {pendingVideoFilesByKey[videoKey].name}</p>
                                ) : null}
                                {videoFile ? (
                                  <button
                                    className="danger-btn sign-video-delete-btn"
                                    type="button"
                                    onClick={() => handleRemoveSignVideoFile(fullVideo)}
                                    disabled={signVideoUploadLoading}
                                  >
                                    <i className="fa-solid fa-trash" aria-hidden="true"></i>
                                    Удалить файл
                                  </button>
                                ) : null}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="sign-video-empty">
                        <p className="word-association-empty">Видео еще не добавлено</p>
                        <input
                          id={`create-sign-video-upload-${sign?.id || sign?.documentId || "unknown"}`}
                          className="sign-video-upload-input"
                          type="file"
                          accept="video/*"
                          onChange={(event) => handleCreateSignVideoFileSelected(sign, event)}
                          disabled={signVideoUploadLoading}
                        />
                        <button
                          className="e-btn sign-video-upload-btn"
                          type="button"
                          onClick={() => handleCreateSignVideoAndUpload(sign)}
                          disabled={signVideoUploadLoading || !pendingCreateVideoFilesBySignKey[String(sign?.id || sign?.documentId || "")]}
                        >
                          <i className="fa-solid fa-upload" aria-hidden="true"></i>
                          {signVideoUploadLoading ? "Загрузка..." : "Отправить на сервер"}
                        </button>
                        {pendingCreateVideoFilesBySignKey[String(sign?.id || sign?.documentId || "")] ? (
                          <p className="sign-video-pending-file">Выбран файл: {pendingCreateVideoFilesBySignKey[String(sign?.id || sign?.documentId || "")].name}</p>
                        ) : null}
                      </div>
                    )}
                  </details>

                  {Array.isArray(sign?.word_associations) && sign.word_associations.length ? (
                    <details className="gesture-complex-item">
                      <summary>
                        <span>Word associations</span>
                        <em>Ассоциации: {sign.word_associations.length}</em>
                      </summary>
                      <div className="word-association-list">
                        {sign.word_associations.map((associationRef) => {
                          const fullAssociation = wordAssociationsById[associationRef.id] || associationRef;
                          const words = normalizeEntityList(fullAssociation?.words);
                          const associatedSigns = normalizeEntityList(fullAssociation?.signs);

                          return (
                            <article className="word-association-card" key={`wa-${associationRef.id}`}>
                              <div className="word-association-title">
                                <i className="fa-solid fa-link" aria-hidden="true"></i>
                                <span>ID: {fullAssociation?.id || "-"}</span>
                                <button
                                  className="word-association-add-sign"
                                  type="button"
                                  onClick={() => openAssociationModal(fullAssociation?.id ? fullAssociation : associationRef)}
                                  title="Редактировать association"
                                >
                                  <i className="fa-solid fa-pen" aria-hidden="true"></i>
                                </button>
                              </div>

                              <div className="sign-video-grid">
                                <div><strong>Document:</strong> {fullAssociation?.documentId || "-"}</div>
                                <div><strong>ID:</strong> {fullAssociation?.id || "-"}</div>
                                <div><strong>Signs:</strong> {associatedSigns.length}</div>
                                {associatedSigns.length ? (
                                  <div className="association-signs-list">
                                    {associatedSigns.map((assocSign, assocSignIndex) => (
                                      <span className="association-sign-tag" key={`assoc-sign-tag-${fullAssociation?.id || associationRef.id}-${assocSign?.id || assocSignIndex}`}>
                                        {assocSign?.name || assocSign?.lemma || `Жест #${assocSign?.id || assocSignIndex + 1}`}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="word-association-empty">Жесты не найдены</p>
                                )}
                                <div><strong>Words:</strong> {words.length}</div>
                                {words.length ? (
                                  <div className="word-tags">
                                    {words.map((word, wordIndex) => (
                                      <span className="word-tag" key={`word-${fullAssociation?.id || associationRef.id}-${word?.id || wordIndex}`}>
                                        {word?.lemma || word?.name || `Word #${word?.id || wordIndex + 1}`}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="word-association-empty">Слова не найдены</p>
                                )}
                              </div>



                              <details className="word-association-json">
                                <summary>JSON</summary>
                                <pre className="gesture-json">{JSON.stringify(fullAssociation, null, 2)}</pre>
                              </details>
                            </article>
                          );
                        })}
                      </div>
                    </details>
                  ) : null}
                </div>

                <details className="gesture-all-json">
                  <summary>Полные данные жеста (JSON)</summary>
                  <pre className="gesture-json">{JSON.stringify(sign, null, 2)}</pre>
                </details>
              </article>
            ))}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        ) : null}
      </div>

      <UniversalModal
        isOpen={associationModalOpen}
        title={`Редактировать association #${currentAssociation?.id || "-"}`}
        onClose={closeAssociationModal}
        footer={(
          <>
            <button className="e-btn" type="button" onClick={closeAssociationModal}>Закрыть</button>
          </>
        )}
      >
        <div className="association-current-grid">
          <section className="association-current-box">
            <h4>Уже добавленные жесты</h4>
            {currentAssociationSigns.length ? (
              <div className="association-current-list">
                {currentAssociationSigns.map((sign, index) => (
                  <div className="association-current-item" key={`current-sign-${sign?.id || index}`}>
                    <span>{sign?.name || sign?.lemma || `Жест #${sign?.id || index + 1}`}</span>
                    <button
                      type="button"
                      className="association-remove-btn"
                      onClick={() => handleRemoveAssociatedSign(currentAssociation, sign)}
                      disabled={associationSignsMutationLoading}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="word-association-empty">Пока нет жестов</p>
            )}
          </section>

          <section className="association-current-box">
            <h4>Уже добавленные words</h4>
            {currentAssociationWords.length ? (
              <div className="association-current-list">
                {currentAssociationWords.map((word, index) => (
                  <div className="association-current-item" key={`current-word-${word?.id || index}`}>
                    <span>{word?.lemma || `Word #${word?.id || index + 1}`}</span>
                    <button
                      type="button"
                      className="association-remove-btn"
                      onClick={() => handleRemoveAssociatedWord(currentAssociation, word)}
                      disabled={associationWordsMutationLoading}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="word-association-empty">Пока нет words</p>
            )}
          </section>
        </div>

        <div className="association-modal-search">
          <label htmlFor="association-sign-search">Жесты: поиск по name contains</label>
          <input
            id="association-sign-search"
            type="text"
            value={associationSignSearchInput}
            onChange={(event) => setAssociationSignSearchInput(event.target.value)}
            placeholder="Введите название жеста"
          />
          <p>Запрос: {formatGestureName(associationSignSearchInput) || "-"}</p>
        </div>

        {associationSignsSearchError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {associationSignsSearchError}</p>
        ) : null}

        {associationSignsMutationError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {associationSignsMutationError}</p>
        ) : null}

        {associationSignsSearchLoading ? (
          <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Ищем жесты...</p>
        ) : null}

        {!!associationSignsSearchResults.length ? (
          <div className="association-modal-results">
            {associationSignsSearchResults.map((sign) => {
              const alreadyLinked = currentAssociationSigns.some((item) => item?.id === sign?.id);

              return (
                <div className="association-modal-result-item" key={`assoc-sign-${sign?.id}`}>
                  <div>
                    <strong>{sign?.name || sign?.lemma || `Жест #${sign?.id}`}</strong>
                    <p>ID: {sign?.id || "-"} | sign_id: {sign?.sign_id || "-"}</p>
                  </div>
                  <button
                    className="e-btn"
                    type="button"
                    onClick={() => handleAttachExistingSign(currentAssociation, sign)}
                    disabled={associationSignsMutationLoading || alreadyLinked}
                  >
                    {alreadyLinked ? "Уже добавлен" : "Добавить"}
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        {!associationSignsSearchLoading && formatGestureName(associationSignSearchInput) && !associationSignsSearchResults.length ? (
          <div className="association-modal-create">
            <p>Жест не найден. Создать новый?</p>
            <label htmlFor="association-create-sign-id">sign_id</label>
            <input
              id="association-create-sign-id"
              type="text"
              value={createAssociationSignId}
              onChange={(event) => setCreateAssociationSignId(event.target.value)}
              placeholder="uuid или id"
            />
            <button
              className="e-btn"
              type="button"
              onClick={handleCreateAndAttachSign}
              disabled={associationSignsMutationLoading || createSignLoading}
            >
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Создать и добавить
            </button>
          </div>
        ) : null}

        <div className="association-modal-search">
          <label htmlFor="association-word-search">Words: поиск по lemma contains</label>
          <input
            id="association-word-search"
            type="text"
            value={associationWordSearchInput}
            onChange={(event) => setAssociationWordSearchInput(event.target.value)}
            placeholder="Введите lemma"
          />
          <p>Запрос: {associationWordQuery || "-"}</p>
        </div>

        {associationWordsSearchError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {associationWordsSearchError}</p>
        ) : null}

        {associationWordsMutationError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {associationWordsMutationError}</p>
        ) : null}

        {associationWordsSearchLoading ? (
          <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Ищем words...</p>
        ) : null}

        {!!associationWordsSearchResults.length ? (
          <div className="association-modal-results">
            {associationWordsSearchResults.map((word) => {
              const alreadyLinked = currentAssociationWords.some((item) => item?.id === word?.id);

              return (
                <div className="association-modal-result-item" key={`assoc-word-${word?.id}`}>
                  <div>
                    <strong>{word?.lemma || `Word #${word?.id}`}</strong>
                    <p>ID: {word?.id || "-"}</p>
                  </div>
                  <button
                    className="e-btn"
                    type="button"
                    onClick={() => handleAttachExistingWord(currentAssociation, word)}
                    disabled={associationWordsMutationLoading || alreadyLinked}
                  >
                    {alreadyLinked ? "Уже добавлен" : "Добавить"}
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        {!associationWordsSearchLoading && associationWordQuery && !hasExactAssociationWordMatch ? (
          <div className="association-modal-create">
            <p>Точного совпадения нет. Создать новый word?</p>
            <button
              className="e-btn"
              type="button"
              onClick={handleCreateAndAttachWord}
              disabled={associationWordsMutationLoading}
            >
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Создать и добавить
            </button>
          </div>
        ) : null}
      </UniversalModal>

      <UniversalModal
        isOpen={signAssociationModalOpen}
        title={`Добавить association к жесту #${activeSignForAssociation?.id || "-"}`}
        onClose={closeSignAssociationModal}
        footer={(
          <>
            <button className="e-btn" type="button" onClick={closeSignAssociationModal}>Закрыть</button>
          </>
        )}
      >
        <div className="association-current-box">
          <h4>Текущие ассоциации жеста</h4>
          {currentSignAssociations.length ? (
            <div className="association-modal-preview-list">
              {currentSignAssociations.map((association, index) => {
                const preview = buildAssociationPreview(association);
                return (
                  <article className="association-modal-preview-card" key={`current-sign-association-${association?.id || index}`}>
                    <div className="sign-video-grid">
                      <div><strong>Association ID:</strong> {preview.id || "-"}</div>
                      <div><strong>Document:</strong> {preview.documentId || "-"}</div>
                      <div><strong>Signs:</strong> {preview.signs.length}</div>
                      <div><strong>Words:</strong> {preview.words.length}</div>
                    </div>
                    {preview.signs.length ? (
                      <div className="association-signs-list">
                        {preview.signs.map((assocSign, assocSignIndex) => (
                          <span className="association-sign-tag" key={`preview-sign-${association?.id || index}-${assocSign?.id || assocSignIndex}`}>
                            {assocSign?.name || assocSign?.lemma || `Жест #${assocSign?.id || assocSignIndex + 1}`}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {preview.words.length ? (
                      <div className="word-tags">
                        {preview.words.map((word, wordIndex) => (
                          <span className="word-tag" key={`preview-word-${association?.id || index}-${word?.id || wordIndex}`}>
                            {word?.lemma || `Word #${word?.id || wordIndex + 1}`}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="word-association-empty">У жеста пока нет ассоциаций</p>
          )}

          <div className="association-create-inline">
            <p>Нужной association нет в списке?</p>
            <button
              className="e-btn"
              type="button"
              onClick={() => handleCreateAssociationForSign(activeSignForAssociation)}
              disabled={signAssociationMutationLoading || !activeSignForAssociation}
            >
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              {signAssociationMutationLoading ? "Создаем..." : "Создать новую association"}
            </button>
          </div>
        </div>

        <div className="association-modal-search-row">
          <label htmlFor="sign-association-search-type">Искать по</label>
          <select
            id="sign-association-search-type"
            value={signAssociationSearchType}
            onChange={(event) => setSignAssociationSearchType(event.target.value === "words" ? "words" : "signs")}
          >
            <option value="signs">Signs</option>
            <option value="words">Words</option>
          </select>
        </div>

        <div className="association-modal-search">
          <label htmlFor="sign-association-search">
            {signAssociationSearchType === "signs" ? "Поиск ассоциаций по signs.name contains" : "Поиск ассоциаций по words.lemma contains"}
          </label>
          <input
            id="sign-association-search"
            type="text"
            value={signAssociationSearchInput}
            onChange={(event) => setSignAssociationSearchInput(event.target.value)}
            placeholder={signAssociationSearchType === "signs" ? "Введите название жеста" : "Введите lemma"}
          />
          <p>Запрос: {formatGestureName(signAssociationSearchInput) || "-"}</p>
        </div>

        {signAssociationSearchError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signAssociationSearchError}</p>
        ) : null}

        {signAssociationMutationError ? (
          <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signAssociationMutationError}</p>
        ) : null}

        {signAssociationSearchLoading ? (
          <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Ищем ассоциации...</p>
        ) : null}

        {!!signAssociationResults.length ? (
          <div className="association-modal-preview-list">
            {signAssociationResults.map((association, index) => {
              const preview = buildAssociationPreview(association);
              const alreadyLinked = currentSignAssociations.some((item) => item?.id === association?.id);

              return (
                <article className="association-modal-preview-card" key={`search-association-${association?.id || index}`}>
                  <div className="sign-video-grid">
                    <div><strong>Association ID:</strong> {preview.id || "-"}</div>
                    <div><strong>Document:</strong> {preview.documentId || "-"}</div>
                    <div><strong>Signs:</strong> {preview.signs.length}</div>
                    <div><strong>Words:</strong> {preview.words.length}</div>
                  </div>

                  {preview.signs.length ? (
                    <div className="association-signs-list">
                      {preview.signs.map((assocSign, assocSignIndex) => (
                        <span className="association-sign-tag" key={`search-sign-${association?.id || index}-${assocSign?.id || assocSignIndex}`}>
                          {assocSign?.name || assocSign?.lemma || `Жест #${assocSign?.id || assocSignIndex + 1}`}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="word-association-empty">Жесты не найдены</p>
                  )}

                  {preview.words.length ? (
                    <div className="word-tags">
                      {preview.words.map((word, wordIndex) => (
                        <span className="word-tag" key={`search-word-${association?.id || index}-${word?.id || wordIndex}`}>
                          {word?.lemma || `Word #${word?.id || wordIndex + 1}`}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="word-association-empty">Слова не найдены</p>
                  )}

                  <button
                    className="e-btn"
                    type="button"
                    onClick={() => handleAttachAssociationToSign(activeSignForAssociation, association)}
                    disabled={signAssociationMutationLoading || alreadyLinked}
                  >
                    {alreadyLinked ? "Уже добавлена" : "Добавить ассоциацию"}
                  </button>
                </article>
              );
            })}
          </div>
        ) : null}
      </UniversalModal>

      <SignTranslationUsageModal
        isOpen={Boolean(usageSign)}
        mode="sign"
        entity={usageSign}
        onClose={() => setUsageSign(null)}
      />
    </main>
  );
};

export default GesturesPage;
